import { ALERT_PRIORITY_LABELS, ALERT_STATE_LABELS, type AlertState, type Severity } from '@convometry/contracts';
import { AlertOctagon, AlertTriangle, BellOff, CheckCircle2, Clock, Flame, Info } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatRelative } from '../../lib/format.js';
import { Badge } from '../primitives/badge.js';

// Alert inbox building blocks. Priority is always icon + text (never color-only).

const PRIORITY_STYLE: Record<Severity, { tone: 'danger' | 'warning' | 'info' | 'neutral'; icon: React.ReactNode }> = {
  critical: { tone: 'danger', icon: <Flame aria-hidden /> },
  high: { tone: 'danger', icon: <AlertOctagon aria-hidden /> },
  medium: { tone: 'warning', icon: <AlertTriangle aria-hidden /> },
  low: { tone: 'info', icon: <Info aria-hidden /> },
};

export interface AlertPriorityBadgeProps {
  priority: Severity;
  className?: string;
}

export function AlertPriorityBadge({ priority, className }: AlertPriorityBadgeProps) {
  const s = PRIORITY_STYLE[priority];
  return (
    <Badge tone={s.tone} icon={s.icon} className={className} data-priority={priority}>
      Prioridad {ALERT_PRIORITY_LABELS[priority].toLowerCase()}
    </Badge>
  );
}

const STATE_STYLE: Record<AlertState, { tone: 'brand' | 'neutral' | 'success' | 'outline'; icon: React.ReactNode }> = {
  open: { tone: 'brand', icon: null },
  acknowledged: { tone: 'outline', icon: null },
  snoozed: { tone: 'neutral', icon: <BellOff aria-hidden /> },
  resolved: { tone: 'success', icon: <CheckCircle2 aria-hidden /> },
  auto_resolved: { tone: 'success', icon: <CheckCircle2 aria-hidden /> },
  expired: { tone: 'neutral', icon: <Clock aria-hidden /> },
};

export function AlertStateBadge({ state, className }: { state: AlertState; className?: string }) {
  const s = STATE_STYLE[state];
  return (
    <Badge tone={s.tone} icon={s.icon ?? undefined} className={className}>
      {ALERT_STATE_LABELS[state]}
    </Badge>
  );
}

export interface AlertItemProps {
  priority: Severity;
  state: AlertState;
  /** Icon cue of the rule (e.g. 🔥), decorative. */
  emoji?: string;
  title: string;
  detail?: string | null;
  contactName: string;
  connectionLabel?: string | null;
  agentName?: string | null;
  openedAt: Date | string;
  snoozedUntil?: Date | string | null;
  now?: Date;
  /** Primary action when the item is clicked (opens the detail). */
  onOpen?: () => void;
  /** Action buttons (acknowledge, snooze, resolve). */
  actions?: React.ReactNode;
  /** Link slot (e.g. "Abrir conversación"). */
  link?: React.ReactNode;
  className?: string;
}

/** One alert row of the inbox. The title is a button when `onOpen` is given. */
export function AlertItem({ priority, state, emoji, title, detail, contactName, connectionLabel, agentName, openedAt, snoozedUntil, now, onOpen, actions, link, className }: AlertItemProps) {
  const meta = [contactName, connectionLabel, agentName ? `Asesor: ${agentName}` : null, formatRelative(openedAt, now)].filter(Boolean).join(' · ');
  const heading = (
    <span className="flex min-w-0 items-center gap-2">
      {emoji ? <span aria-hidden>{emoji}</span> : null}
      <span className="truncate">{title}</span>
    </span>
  );
  return (
    <article
      data-state={state}
      className={cn('grid gap-2 rounded-card border border-border-subtle bg-surface-default p-4 shadow-card', state === 'open' && 'border-l-4 border-l-brand-primary', className)}
      aria-label={`${title}: ${contactName}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <AlertPriorityBadge priority={priority} />
        <AlertStateBadge state={state} />
        {state === 'snoozed' && snoozedUntil ? <span className="text-helper text-text-secondary">hasta {new Date(snoozedUntil).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</span> : null}
      </div>
      <h3 className="text-body font-semibold text-text-primary">
        {onOpen ? (
          <button type="button" onClick={onOpen} className="max-w-full rounded-control text-left hover:underline focus-visible:outline-2 focus-visible:outline-focus">
            {heading}
          </button>
        ) : (
          heading
        )}
      </h3>
      {detail ? <p className="text-body text-text-secondary">{detail}</p> : null}
      <p className="text-helper text-text-secondary">{meta}</p>
      {actions || link ? (
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {link ? <span className="ml-auto">{link}</span> : null}
        </div>
      ) : null}
    </article>
  );
}

/** Accessible count badge for navigation (e.g. active alerts). Renders nothing for 0. */
export function CountBadge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return (
    <span className="cm-tabular inline-flex min-w-5 items-center justify-center rounded-pill bg-danger px-1.5 text-helper font-semibold text-brand-on-primary" aria-label={`${count} ${label}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
}
